import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

/**
 * 动态菜单标题回退（与 base_menu.code 一致）。
 * 仅当库中 name 缺失或因导入编码错误显示为问号时使用，避免覆盖菜单管理中已修改的正常名称。
 */
const SERVER_MENU_META_TITLE_FALLBACK: Record<string, string> = {
  customerWalletManage: '客户钱包管理',
  operationManage: '运营管理',
  customerFeedbackManage: '客户反馈管理',
  customerComplaintManage: '客户投诉管理',
  customerComplaint: '客户投诉',
  driverEvaluationManage: '司机评价管理',
  driverEvaluation: '司机评价',
  accessoryManage: '配件管理',
  accessoryManageIndex: '配件管理',
  aiAssistantManage: 'AI 助手管理',
  knowledgeBaseManage: '知识库管理',
  aiUsageLogManage: '使用与日志'
}

/** 标题为空或乱码（? / �）时才允许用硬编码回退 */
const needsMenuTitleFallback = (title?: string) =>
  !title?.trim() || /^[?\uFFFD\s]+$/.test(title)

const shouldUseCouponMenuIcon = (route: AppCustomRouteRecordRaw) => {
  const component = (route.component || '') as string
  const path = (route.path || '') as string
  const name = (route.name || '') as string

  return (
    /OperationManage\/Coupon(Manage|GrantRecord|WriteOffRecord)/.test(component) ||
    /coupon(Manage|GrantRecord|WriteOffRecord)/i.test(path) ||
    /coupon(Manage|GrantRecord|WriteOffRecord)/i.test(name)
  )
}

const applyServerMenuTitleFallback = (route: AppCustomRouteRecordRaw) => {
  const code = route.name as string
  const fb = code ? SERVER_MENU_META_TITLE_FALLBACK[code] : undefined
  const useTitleFallback = !!(fb && needsMenuTitleFallback(route.meta?.title as string | undefined))
  const iconFallback = shouldUseCouponMenuIcon(route) ? 'ep:chat-dot-round' : undefined

  if (useTitleFallback || iconFallback) {
    route.meta = {
      ...(route.meta || {}),
      ...(useTitleFallback ? { title: fb } : {}),
      ...((iconFallback && !route.meta?.icon) ? { icon: iconFallback } : {})
    }
  }
  route.children?.forEach((child) => applyServerMenuTitleFallback(child))
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []
  for (const route of routes) {
    // 按钮权限仅用于 hasPermi，不能注册为 Vue 路由（无 component 会导致登录后白屏）
    if ((route as any).type === 'btn') {
      continue
    }
    applyServerMenuTitleFallback(route)
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: {
        ...route.meta,
        id: route.id,
        type: (route as any).type,
        alwaysShow: true
      }
    }
    // 统一驾驶舱全屏：隐藏内容区内边距，配合 AppView hideLayout（含子路由）
    if (
      route.name === 'unifiedCockpit' ||
      (route as any).code === 'unifiedCockpit' ||
      ((route as any).type === 'menu' &&
        String(route.component || '').includes('Cockpit/index'))
    ) {
      data.meta = {
        ...data.meta,
        hideLayout: true
      }
    }
    if (route.component) {
      const comModule = modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`]
      const component = route.component as string
      if (!comModule && !component.includes('#') && !component.includes('##')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
      } else {
        // # 顶级目录用 Layout；## 子目录用 ParentLayout（嵌套 router-view）
        if (component === '##' || component.includes('##')) {
          data.component = getParentLayout()
        } else if (component === '#' || component.includes('#')) {
          data.component = Layout
        } else {
          data.component = comModule
        }
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    const hasComponent = !!data.component
    const hasRedirect = !!data.redirect
    const hasChildren = !!(data.children && data.children.length)
    if (!hasComponent && !hasRedirect && !hasChildren) {
      console.warn('[router] 跳过无效菜单路由:', route.name)
      continue
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}

// 前端添加重定向字段
export const addRedirectField = (data) => {
  // 递归查找最深层的第一个子元素路径
  function getDeepestPath(item) {
    // 如果没有子元素，返回当前路径
    if (!item.children || item.children.length === 0) {
      return item.path || ''
    }

    // 获取第一个子元素
    const firstChild = item.children[0]

    // 递归获取最深层路径
    const deepestPath = getDeepestPath(firstChild)

    // 拼接当前路径和最深层路径
    if (!item.path) return deepestPath
    if (!deepestPath) return item.path

    // 确保路径格式正确
    const parentPath = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path
    const childPath = deepestPath.startsWith('/') ? deepestPath : '/' + deepestPath

    return `${parentPath}${childPath}`
  }

  return data.map((item) => {
    // 深拷贝当前项以避免修改原数据
    const newItem = { ...item }

    // 递归处理子级
    if (newItem.children && newItem.children.length > 0) {
      newItem.children = addRedirectField(newItem.children)

      // 获取最深层路径作为redirect
      const redirectPath = getDeepestPath(newItem)
      if (redirectPath) {
        newItem.redirect = redirectPath
      }
    }

    return newItem
  })
}

// export const addRedirectField = (data) => {
//   // 递归查找最深层的第一个子元素路径
//   function getDeepestPath(item) {
//     // 如果没有子元素，返回当前路径
//     if (!item.children || item.children.length === 0) {
//       return item.path || ''
//     }

//     // 获取第一个子元素（注意：这里现在是排序后的第一个元素）
//     const firstChild = item.children[0]

//     // 递归获取最深层路径
//     const deepestPath = getDeepestPath(firstChild)

//     // 拼接当前路径和最深层路径
//     if (!item.path) return deepestPath
//     if (!deepestPath) return item.path

//     // 确保路径格式正确
//     const parentPath = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path
//     const childPath = deepestPath.startsWith('/') ? deepestPath : '/' + deepestPath

//     return `${parentPath}${childPath}`
//   }

//   // 按sort字段排序，统一从小到大
//   const sortedData = [...data].sort((a, b) => {
//     const sortA = a.sort ?? 0;
//     const sortB = b.sort ?? 0;
//     return sortA - sortB;
//   });

//   return sortedData.map((item) => {
//     // 深拷贝当前项以避免修改原数据
//     const newItem = { ...item }

//     // 递归处理子级
//     if (newItem.children && newItem.children.length > 0) {
//       // 对子级也进行排序
//       newItem.children = addRedirectField(newItem.children);

//       // 获取最深层路径作为redirect
//       const redirectPath = getDeepestPath(newItem);
//       if (redirectPath) {
//         newItem.redirect = redirectPath;
//       }
//     }
//     return newItem;
//   });
// };
